import { toast } from "sonner";
import { ArrowUpRight } from "lucide-react";
import { useLang } from "@/lib/lang";
import { useStoreLinks } from "@/lib/storeLinks";

export const AppleIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true">
    <path d="M16.365 1.43c0 1.14-.493 2.27-1.177 3.08-.744.9-1.99 1.57-2.987 1.57-.12 0-.23-.02-.3-.03-.01-.06-.04-.22-.04-.39 0-1.15.572-2.27 1.206-2.98.804-.94 2.142-1.64 3.248-1.68.03.13.05.28.05.43zm4.565 15.71c-.03.07-.463 1.58-1.518 3.12-.945 1.34-1.94 2.71-3.43 2.71-1.517 0-1.9-.88-3.63-.88-1.698 0-2.302.91-3.67.91-1.377 0-2.332-1.26-3.428-2.8-1.287-1.82-2.323-4.63-2.323-7.28 0-4.28 2.797-6.55 5.552-6.55 1.448 0 2.675.95 3.6.95.865 0 2.222-1.01 3.902-1.01.613 0 2.886.06 4.374 2.19-.13.09-2.383 1.37-2.383 4.19 0 3.26 2.854 4.42 2.955 4.45z" />
  </svg>
);

export const PlayIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
    <path fill="#22d3ee" d="M3.6 1.8 13.8 12 3.6 22.2c-.4-.2-.6-.6-.6-1.1V2.9c0-.5.2-.9.6-1.1z" />
    <path fill="#5cf2a0" d="M17.3 8.5 13.8 12 3.6 1.8c.3-.2.8-.2 1.2 0l12.5 6.7z" />
    <path fill="#f472b6" d="M17.3 15.5 4.8 22.2c-.4.2-.9.2-1.2 0L13.8 12l3.5 3.5z" />
    <path fill="#fbbf24" d="m20.9 10.4-3.6-1.9L13.8 12l3.5 3.5 3.6-1.9c1.2-.7 1.2-2.5 0-3.2z" />
  </svg>
);

export const StoreBadges = ({ app, testPrefix = "app" }) => {
  const { t } = useLang();
  const { google, apple } = useStoreLinks(app.slug);
  const items = [
    { key: "google-play", Icon: PlayIcon, top: t.badges.getItOn, name: "Google Play", url: google, note: app.status === "pending" ? t.badges.pending : t.badges.soon },
    { key: "app-store", Icon: AppleIcon, top: t.badges.downloadOn, name: "App Store", url: apple, note: t.badges.soon },
  ];
  const cls = "group flex items-center gap-3 rounded-2xl border px-4 py-2.5 text-left transition-[transform,background-color,border-color] duration-500 ease-[cubic-bezier(.16,1,.3,1)] hover:-translate-y-1";
  return (
    <div className="flex flex-wrap gap-3">
      {items.map(({ key, Icon, top, name, url, note }) => {
        const inner = (
          <>
            <Icon className="h-6 w-6 text-white" />
            <span className="flex flex-col leading-none">
              <span className="text-[10px] uppercase tracking-[0.15em] text-white/50">{top}</span>
              <span className="mt-1 font-display text-base font-semibold">{name}</span>
            </span>
          </>
        );
        return url ? (
          <a key={key} href={url} target="_blank" rel="noopener noreferrer" data-testid={`${testPrefix}-${key}-button`} className={`${cls} border-white/25 bg-white/[0.08] hover:border-white/50 hover:bg-white/[0.14]`}>
            {inner}
            <ArrowUpRight className="ml-1 h-4 w-4 text-white/60 transition-transform duration-500 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
          </a>
        ) : (
          <button key={key} type="button" aria-disabled="true" data-testid={`${testPrefix}-${key}-button`} onClick={() => toast(`${name} — ${note}`)} className={`${cls} border-white/15 bg-white/[0.04] hover:border-white/35 hover:bg-white/[0.08]`}>
            {inner}
            <span className="ml-1 rounded-full border border-[#5cf2a0]/30 bg-[#5cf2a0]/10 px-2 py-1 font-mono text-[9px] uppercase tracking-[0.15em] text-[#8ff7c4]">{note}</span>
          </button>
        );
      })}
    </div>
  );
};
